import React from 'react';
import Tasks from '../Tasks';
import {
  Link,
  matchPath,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import {Box, Flex, SimpleGrid} from '@chakra-ui/react';
import {PageHeader} from '../PageHeader';
import {Signals} from '../Debug/Signals';
import {Provisioning} from '../Debug/Provisioning';
import {AiDebug} from '../Debug/AiDebug';
import {GenerateResearchPrompt} from '../Debug/GenerateResearchPrompt';
import {ValidateDomains} from '../Debug/ValidateDomains';
import {AiChatBot} from '../Debug/AiChatBot';
import {SegmentationChat} from '../Debug/SegmentationChat/SegmentationChat';
import {
  Task,
  UserAdmin,
  ChartLine,
  CheckmarkOutline,
  Debug,
  Chat,
  CategoryNewEach,
  DocumentTasks,
  ArrowLeft,
  Activity,
} from '@carbon/icons-react';
import {IntentRollout} from '../Debug/IntentRollout';

const adminTabGroups = [
  {
    section: 'System',
    tabs: [
      {label: 'Tasks', path: 'tasks', icon: <Task />},
      {label: 'Provisioning', path: 'provisioning', icon: <UserAdmin />},
      {label: 'Intent Rollout', path: 'intent-rollout', icon: <Activity />},
    ],
  },
  {
    section: 'Data Tools',
    tabs: [
      {label: 'Signals', path: 'signals', icon: <ChartLine />},
      {
        label: 'Validate Domains',
        path: 'validate-domains',
        icon: <CheckmarkOutline />,
      },
    ],
  },
  {
    section: 'AI Tools',
    tabs: [
      {label: 'AI Debug', path: 'ai-debug', icon: <Debug />},
      {label: 'AI Chat Bot', path: 'ai-chat-bot', icon: <Chat />},
      {
        label: 'Segmentation Chat',
        path: 'segmentation-chat',
        icon: <CategoryNewEach />,
      },
      {
        label: 'Research Prompt',
        path: 'research-prompt',
        icon: <DocumentTasks />,
      },
    ],
  },
];

const allTabs = adminTabGroups.flatMap((g) => g.tabs);

const AdminLayout = () => {
  const location = useLocation();
  const currentTab = allTabs.find((tab) =>
    matchPath(`/admin/${tab.path}/*`, location.pathname)
  );
  const title = currentTab?.label ?? 'Unknown';

  return (
    <Flex direction="column" h="100%">
      <Flex
        alignItems="center"
        bgColor="kgray.100"
        h={10}
        px={4}
        gap={2}
        fontSize="md"
        fontWeight={500}
        flexShrink={0}
        borderBottomWidth="1px"
        borderBottomColor="kgray.200"
      >
        <Box
          as={Link}
          to="/admin"
          color="kgray.300"
          _hover={{color: 'kgray.400'}}
          display="flex"
          alignItems="center"
          gap={2}
        >
          <ArrowLeft size={16} />
          Admin
        </Box>
        <Box as="span" color="kgray.300">
          /
        </Box>
        <Box as="span">{title}</Box>
      </Flex>
      <Box flex="1" overflow="hidden">
        <Outlet />
      </Box>
    </Flex>
  );
};

const AdminLanding = () => {
  return (
    <Box>
      <PageHeader title="Admin" />
      <Box maxW="800px" mx="auto" my={8} px={4}>
        {adminTabGroups.map(({section, tabs}) => (
          <Box key={section} mb={8}>
            <Box
              fontSize="sm"
              fontWeight="normal"
              color="kgray.300"
              textTransform="uppercase"
              mb={4}
            >
              {section}
            </Box>
            <SimpleGrid columns={2} spacing={4}>
              {tabs.map((tab) => (
                <Box
                  as={Link}
                  to={tab.path}
                  key={tab.path}
                  p={4}
                  borderWidth="1px"
                  borderColor="kgray.200"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  gap={3}
                  _hover={{
                    borderColor: 'kblue.300',
                    bg: 'kgray.50',
                  }}
                >
                  <Box color="kgray.300">{tab.icon}</Box>
                  <Box fontWeight="500" color="kgray.400">
                    {tab.label}
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Admin = () => {
  return (
    <Routes>
      <Route index element={<AdminLanding />} />
      <Route element={<AdminLayout />}>
        <Route path="/provisioning/*" element={<Provisioning />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/intent-rollout" element={<IntentRollout />} />
        <Route path="/signals" element={<Signals />} />
        <Route path="/validate-domains" element={<ValidateDomains />} />
        <Route path="/ai-debug" element={<AiDebug />} />
        <Route path="/ai-chat-bot" element={<AiChatBot />} />
        <Route path="/segmentation-chat" element={<SegmentationChat />} />
        <Route path="/research-prompt" element={<GenerateResearchPrompt />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  );
};

export default Admin;
